/*
 * Copyright '2024-25' Dell Inc. or its subsidiaries. All Rights Reserved.
 */
import { IBrowserNavigationGateHandler } from "sirius-platform-support-library/shared/browser-events/browser-navigation-gate-handler.interface";
import { IAuthenticationService } from "sirius-platform-support-library/shared/authentication/authentication-service.interface";
import { IBrowserNavigationEventsReceiver } from "sirius-platform-support-library/shared/browser-events/browser-navigation-events-receiver.interface";
import { RoutingUtilities } from "sirius-platform-support-library/utilities/routing-utilities";
import { pathToRegexp } from "path-to-regexp";
import { environment } from "../../../environments/environments";

export enum NavigationActionType {
  ALLOW = "ALLOW",
  BLOCK = "BLOCK",
  REDIRECT_TO_LOGIN = "REDIRECT_TO_LOGIN",
  REDIRECT_TO_PORTAL = "REDIRECT_TO_PORTAL",
  REDIRECT_TO_DASHBOARD = "REDIRECT_TO_DASHBOARD",
  REDIRECT_TO_INVALID_EMAIL_DOMAIN = "REDIRECT_TO_INVALID_EMAIL_DOMAIN",
  REDIRECT_TO_INACTIVITY = "REDIRECT_TO_INACTIVITY",
  REDIRECT_TO_ATTESTATION = "REDIRECT_TO_ATTESTATION",
  REDIRECT_TO_CONTACT_US = "REDIRECT_TO_CONTACT_US",
}

export interface NavigationAction {
  type: NavigationActionType;
  nextUrl?: string;
}

export const UserActivityNavigationGateHandlerTypeName =
  "UserActivityNavigationGateHandler";

export class UserActivityNavigationGateHandler
  implements IBrowserNavigationGateHandler, IBrowserNavigationEventsReceiver
{
  private static readonly USER_STATUS_CLAIM: string = "userStatus";
  private static readonly IS_EMAIL_DOMAIN_ALLOWED_CLAIM: string =
    "isEmailDomainAllowed";
  private static readonly IS_LONG_TERM_INACTIVE_CLAIM: string =
    "isLongTermInactive";
  private static readonly IS_ATTESTATION_EXPIRED_CLAIM: string =
    "isAttestationExpired";
  private static readonly COMPANY_HAS_ACTIVE_ADMIN_CLAIM: string =
    "companyHasActiveAdmin";

  private static readonly NAVIGATION_ACTION_URLS = {
    [NavigationActionType.ALLOW]: null,
    [NavigationActionType.BLOCK]: null,
    [NavigationActionType.REDIRECT_TO_LOGIN]: "/login",
    [NavigationActionType.REDIRECT_TO_PORTAL]: "/portal",
    [NavigationActionType.REDIRECT_TO_DASHBOARD]: "/dashboard",
    [NavigationActionType.REDIRECT_TO_INVALID_EMAIL_DOMAIN]:
      "/confirmation/invalid-email-domain",
    [NavigationActionType.REDIRECT_TO_INACTIVITY]: "/confirmation/inactivity",
    [NavigationActionType.REDIRECT_TO_ATTESTATION]: "/confirmation/attestation",
    [NavigationActionType.REDIRECT_TO_CONTACT_US]: "/contact-us",
  };

  private static readonly EXCLUDED_NAVIGATION_URLS = [
    UserActivityNavigationGateHandler.NAVIGATION_ACTION_URLS[
      NavigationActionType.REDIRECT_TO_PORTAL
    ],
    UserActivityNavigationGateHandler.NAVIGATION_ACTION_URLS[
      NavigationActionType.REDIRECT_TO_DASHBOARD
    ],
  ];

  private readonly window: Window;
  private readonly authenticationService: IAuthenticationService;

  private navigationAction: NavigationAction = {
    type: NavigationActionType.ALLOW,
    nextUrl: null,
  };

  public constructor(
    window: Window,
    authenticationService: IAuthenticationService
  ) {
    this.window = window;
    this.authenticationService = authenticationService;
  }

  public getUniqueId(): string {
    return UserActivityNavigationGateHandlerTypeName;
  }

  public onBeforeNavigate(url: string): void | Promise<void> {
    const normalizedUrl = RoutingUtilities.sanitizeAndParseUrl(url);
    const urls = UserActivityNavigationGateHandler.NAVIGATION_ACTION_URLS;
    const allowedRoutes = Object.values(urls)
      .filter((v) => v)
      .filter(
        (v) =>
          UserActivityNavigationGateHandler.EXCLUDED_NAVIGATION_URLS.indexOf(
            v
          ) === -1
      );
    // If we want to navigate to a route other than one in the list of possible special routes
    if (!this.matchesAnyRoutePattern(normalizedUrl.pathname, allowedRoutes)) {
      // Decide the peculiar case navigation action
      this.navigationAction = this.getNavigationAction(url);
      // If it is login we treat it specially
      if (
        this.navigationAction.type === NavigationActionType.REDIRECT_TO_LOGIN
      ) {
        this.authenticationService.silentSignIn(true).catch((e) => {
          console.debug("Failed to silent sign in (non-interactive)", e);
          this.authenticationService
            .signIn()
            .catch((e) => console.debug("Failed to sign in (interactive)", e));
        });
      } else if (this.navigationAction.nextUrl) {
        const normalizedNextUrl = RoutingUtilities.sanitizeAndParseUrl(
          this.navigationAction.nextUrl
        );
        // If we are on the same origin, we can push the state
        if (
          normalizedNextUrl.origin.toLowerCase() ===
          this.window.location.origin.toLowerCase()
        ) {
          const route = `${normalizedNextUrl.pathname}${normalizedNextUrl.search}`;
          this.window.history.pushState("", "", route);
        } else {
          // Else we redirect to the next external page
          this.window.location.href = normalizedNextUrl.toString();
        }
      }
    }
  }

  public isNavigationAllowed(url: string, hardNavigation: boolean): boolean {
    try {
      const normalizedUrl = RoutingUtilities.sanitizeAndParseUrl(url);
      // Allow navigation to the next page if the origin is different - to an external page
      if (
        normalizedUrl.origin.toLowerCase() !==
        this.window.location.origin.toLowerCase()
      ) {
        return true;
      }

      // Allow navigation to the next page if the route is allowed from the list of possible special routes
      const allowedRoutes = Object.values(
        UserActivityNavigationGateHandler.NAVIGATION_ACTION_URLS
      ).filter((v) => v);
      if (this.matchesAnyRoutePattern(normalizedUrl.pathname, allowedRoutes)) {
        return true;
      }

      if (!this.navigationAction) {
        return true;
      }

      // Handle peculiar case when user is not allowed to navigate to the next page based on the navigation action
      switch (this.navigationAction.type) {
        case NavigationActionType.ALLOW:
        case NavigationActionType.REDIRECT_TO_LOGIN:
          return true;
        case NavigationActionType.BLOCK:
          return false;
      }

      // Always default to true to not break the navigation
      return true;
    } catch (e) {
      console.error(e);

      // Always default to true to not break the navigation
      return true;
    } finally {
      // Clear peculiar case navigation action
      this.navigationAction = null;
    }
  }

  public onAfterNavigate(url: string): void | Promise<void> {}

  private getNavigationAction(url: string): NavigationAction {
    const normalizedUrl = RoutingUtilities.sanitizeAndParseUrl(url);

    const userContext = this.authenticationService.getUserContext();

    const userStatus =
      userContext.claims[UserActivityNavigationGateHandler.USER_STATUS_CLAIM] ||
      "NO_SESSION";
    const isEmailDomainAllowed =
      userContext.claims[
        UserActivityNavigationGateHandler.IS_EMAIL_DOMAIN_ALLOWED_CLAIM
      ] ?? true;
    const isLongTermInactive =
      userContext.claims[
        UserActivityNavigationGateHandler.IS_LONG_TERM_INACTIVE_CLAIM
      ]?.toString() == "true";
    const isAttestationExpired =
      userContext.claims[
        UserActivityNavigationGateHandler.IS_ATTESTATION_EXPIRED_CLAIM
      ]?.toString() == "true";
    const companyHasActiveAdmin =
      userContext.claims[
        UserActivityNavigationGateHandler.COMPANY_HAS_ACTIVE_ADMIN_CLAIM
      ]?.toString() == "true";

    let navigationType: NavigationActionType;

    if (userStatus === "NO_SESSION") {
      if (
        normalizedUrl.origin.toLowerCase() ===
        window.location.origin.toLowerCase()
      ) {
        const route = normalizedUrl.pathname;
        if (route === "" || route === "/") {
          navigationType = NavigationActionType.REDIRECT_TO_PORTAL;
        } else if (
          this.matchesAnyRoutePattern(route, environment.unauthenticatedRoutes)
        ) {
          navigationType = NavigationActionType.ALLOW;
        } else {
          navigationType = NavigationActionType.REDIRECT_TO_LOGIN;
        }
      } else {
        navigationType = NavigationActionType.ALLOW;
      }
    } else if (!isEmailDomainAllowed) {
      navigationType = NavigationActionType.REDIRECT_TO_INVALID_EMAIL_DOMAIN;
    } else if (userStatus === "INACTIVE") {
      if (!isLongTermInactive && companyHasActiveAdmin) {
        navigationType = NavigationActionType.REDIRECT_TO_INACTIVITY;
      } else {
        navigationType = NavigationActionType.REDIRECT_TO_CONTACT_US;
      }
    } else if (environment.companyAttestationEnabled && isAttestationExpired) {
      navigationType = NavigationActionType.REDIRECT_TO_ATTESTATION;

      const encodedCurrentRouteWith = encodeURIComponent(
        this.window.location.href
      );
      const route =
        UserActivityNavigationGateHandler.NAVIGATION_ACTION_URLS[
          navigationType
        ] + `?redirectUrl=${encodedCurrentRouteWith}`;

      return {
        type: navigationType,
        nextUrl: route,
      };
    } else {
      if (
        normalizedUrl.origin.toLowerCase() ===
        window.location.origin.toLowerCase()
      ) {
        const route = normalizedUrl.pathname;
        if (route === "" || route === "/") {
          navigationType = NavigationActionType.REDIRECT_TO_DASHBOARD;
        } else {
          navigationType = NavigationActionType.ALLOW;
        }
      } else {
        navigationType = NavigationActionType.ALLOW;
      }
    }

    return {
      type: navigationType,
      nextUrl:
        UserActivityNavigationGateHandler.NAVIGATION_ACTION_URLS[
          navigationType
        ],
    };
  }

  private matchesAnyRoutePattern(
    givenRoute: string,
    routesPatterns: string[]
  ): boolean {
    return !!this.getMatchingRoutePattern(givenRoute, routesPatterns);
  }

  private getMatchingRoutePattern(
    givenRoute: string,
    routesPatterns: string[]
  ): string | undefined {
    if (!givenRoute) {
      return undefined;
    }
    for (const routePattern of routesPatterns) {
      try {
        const routeTemplate = routePattern.replaceAll("*", "(.*)");
        const expression = pathToRegexp(routeTemplate, undefined, {
          end: true,
        });
        if (expression.test(givenRoute)) {
          return routeTemplate;
        }
      } catch (e) {
        console.error(e);
      }
    }
    return undefined;
  }
}
